import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { RouterModule } from '@angular/router'
import { Permission } from '@employer/app/config/permission.config'
import { EmployerNavigationService, ROUTES } from '@employer/app/services/navigation.service'
import { TranslateModule } from '@ngx-translate/core'
import { Observable, combineLatest, map, of } from 'rxjs'

export type SectionId = 'openings' | 'engage' | 'account'

export interface SectionTab {
  label: string
  route: string
  queryParams?: Record<string, string>
  isVisible$: Observable<boolean>
  /**
   * Match the URL exactly rather than as a prefix. Needed where a tab's route is a
   * prefix of its siblings' — `/account` is the start of `/account/security`, so the
   * default subset match would leave Personal Information lit on every account tab.
   */
  exact?: boolean
}

const ARIA_LABELS: Record<SectionId, string> = {
  openings: 'Openings sections',
  engage: 'Pipelines sections',
  account: 'My Account sections',
}

/**
 * The tab row that replaced the Openings and Engage nav groups. Both collapsed to a
 * single leaf each (see `navigation.service.ts`), so the leaves they used to hold —
 * Templates, Applicant Search, Talent Pool, Prospects — are tabs here instead.
 *
 * These are real links, not a switched container: every destination keeps its own
 * route, guard and lazy module, so deep links and permissions behave exactly as they
 * did when each was a nav row. Only the way you reach them changed.
 *
 * Each section carries its own accent so the two screens read apart at a glance:
 * Engage takes the primary accent, Openings the secondary.
 *
 * My Account is the third section (CNCT-6812). Its details — personal information,
 * decision team settings, profile picture, security, notifications — each had their own
 * route all along, but nothing linked to them once the sidebar's My Account subtree was
 * removed, so /account showed personal information and nothing else. The tab row is how
 * you reach the rest again.
 */
@Component({
  selector: 'section-tabs',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  // The tab list is built once in ngOnInit and never reassigned, so there is
  // nothing for the default strategy to re-check on every tick.
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="section-tabs" [class.section-tabs--openings]="section === 'openings'" [attr.aria-label]="ariaLabel">
      <ng-container *ngFor="let tab of tabs; trackBy: trackByRoute">
        <a
          *ngIf="tab.isVisible$ | async"
          class="section-tab"
          [routerLink]="tab.route"
          [queryParams]="tab.queryParams ?? null"
          routerLinkActive="section-tab--active"
          #rla="routerLinkActive"
          [routerLinkActiveOptions]="tab.exact ? exactMatch : subsetMatch"
          [attr.aria-current]="rla.isActive ? 'page' : null"
        >
          {{ tab.label | translate }}
        </a>
      </ng-container>
    </nav>
  `,
  styles: [
    `
      /* Sits directly under the page title, above the screen's own controls. The
         bottom rule runs the full width of the pane and each tab's active underline
         overlaps it, so the two read as one edge rather than two stacked lines. */
      /* Continues the white band the title area starts, and owns the divider: on the host it spans
         the full content width, where on the row itself it would stop at the gutter.
         The gutter is padding on the row, not the host, so the row scrolls right to the pane's edge
         instead of clipping its last tab a gutter short of it. */
      :host {
        display: block;
        margin: 0 -1rem 1.4rem;
        background: rgb(var(--color-white));
        border-bottom: 1px solid rgb(var(--color-app-borders));
      }

      @media (min-width: 768px) {
        :host {
          margin: 0 -1.5rem 1.4rem;
        }
        .section-tabs {
          padding: 0 1.5rem;
        }
      }

      /* overflow-x: auto makes overflow-y auto too, so nothing may overhang the row vertically or it
         turns into a vertical scroller that clips the underline. The row itself is pulled 1px down
         onto the host's border instead of each tab — the active underline still covers the rule,
         and the tabs fit inside the row. overflow-y is pinned to hidden as a second guard. */
      .section-tabs {
        display: flex;
        gap: 2px;
        padding: 0 1rem;
        margin-bottom: -1px;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
      }
      .section-tabs::-webkit-scrollbar {
        height: 0;
      }

      .section-tab {
        padding: 11px 15.4px;
        font-size: 14.3px;
        /* 1.55, not 1.4: the mockup's tabs inherit the body line-height, and the shorter line was
           leaving the row ~2px under its height. */
        line-height: 1.55;
        white-space: nowrap;
        color: rgb(var(--color-text-base));
        text-decoration: none;
        border-bottom: 2px solid transparent;
        cursor: pointer;
        transition: color 0.13s ease;
      }
      .section-tab:hover {
        color: rgb(var(--color-text-dark));
      }
      .section-tab:focus-visible {
        outline: 2px solid rgb(var(--color-primary-accent));
        outline-offset: -2px;
        border-radius: 4px 4px 0 0;
      }

      /* Active tab: the section's accent, and the label takes the demibold face —
         font-weight alone would not bolden the AvenirNext-era theme. */
      .section-tab--active {
        color: rgb(var(--color-text-dark));
        font-family: var(--font-primary-demibold, inherit);
        font-weight: 600;
        border-bottom-color: rgb(var(--color-primary-accent));
      }
      .section-tabs--openings .section-tab--active {
        border-bottom-color: rgb(var(--color-secondary));
      }

      /* On a phone the row pins to the top of the scroll area once the title has
         scrolled past, so you never lose track of which tab you are on. Kept one step
         below the header's own z-12 stacking context (CNCT-6845): the account menu opens
         from inside that header and hangs down over this row, so the row must not outrank
         it. It still clears the page content scrolling beneath it.
         top is -1rem, not 0: sticky offsets are measured inside the scroll container's
         padding, and main-layout's content gutter is pt-4 (1rem) below md — at 0 the row
         pinned 1rem low and content scrolled through the gap above it (CNCT-6860). */
      @media (max-width: 767px) {
        :host {
          position: sticky;
          top: -1rem;
          z-index: 11;
          box-shadow: 0 4px 12px -8px rgba(35, 48, 61, 0.25);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .section-tab {
          transition: none;
        }
      }
    `,
  ],
})
export class SectionTabsComponent implements OnInit {
  @Input({ required: true }) section!: SectionId

  /**
   * Built ONCE, in ngOnInit.
   *
   * This was previously a getter the template read directly. That is an infinite
   * change-detection loop: the getter returned a fresh array of fresh objects
   * holding freshly-constructed observables on every call, so each tick gave
   * *ngFor new identities to re-render and gave every async pipe a new source to
   * resubscribe to — and each new subscription emitting marked the view dirty
   * again. The tab list is static per section, so it is computed once and held.
   */
  tabs: SectionTab[] = []

  ariaLabel = ''

  /* Held as fields, not object literals in the template: a fresh literal on every
     check is a new identity for routerLinkActive to react to. */
  readonly subsetMatch = { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const
  readonly exactMatch = { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const

  constructor(private navigationService: EmployerNavigationService) {}

  ngOnInit(): void {
    this.tabs = this.buildTabs()
    this.ariaLabel = ARIA_LABELS[this.section]
  }

  private buildTabs(): SectionTab[] {
    switch (this.section) {
      case 'openings':
        return this.buildOpeningsTabs()
      case 'account':
        return this.buildAccountTabs()
      default:
        return this.buildEngageTabs()
    }
  }

  trackByRoute = (_: number, tab: SectionTab) => tab.route

  /** Manage is the landing tab, so the leaf row and this tab share a destination. */
  private buildOpeningsTabs(): SectionTab[] {
    return [
      {
        label: 'Manage',
        route: ROUTES.JOBS_MANAGE,
        isVisible$: this.navigationService.userHasPermission$(Permission.ViewJobs),
      },
      {
        label: 'Templates',
        route: ROUTES.JOBS_TEMPLATE,
        isVisible$: this.navigationService.userHasPermission$(Permission.ManageJobTemplates),
      },
      {
        label: 'Settings',
        route: ROUTES.OPENINGS_SETTINGS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ManageCompanySettings),
      },
    ]
  }

  private buildEngageTabs(): SectionTab[] {
    const poolVisible$ = combineLatest([
      this.navigationService.userHasPermission$(Permission.ViewCandidatePool),
      this.navigationService.userHasPermission$(Permission.ManageCandidatePoolShares),
    ]).pipe(map(([view, share]) => view || share))

    return [
      {
        label: 'Pipelines',
        route: ROUTES.JOBS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ViewJobs),
      },
      {
        label: 'Applicant Search',
        route: ROUTES.APPLICATIONS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ViewApplications),
      },
      {
        label: 'Talent Pool',
        route: ROUTES.CANDIDATE_POOL,
        isVisible$: poolVisible$,
      },
      {
        label: 'Prospects',
        route: ROUTES.STUB_ENGAGE_PROSPECTS,
        isVisible$: combineLatest([
          this.navigationService.features$('employerEngageProspects'),
          this.navigationService.userHasPermission$(Permission.ViewCandidatePool),
        ]).pipe(map(([flagOn, canView]) => flagOn && canView)),
      },
      {
        label: 'Settings',
        route: ROUTES.ENGAGE_SETTINGS,
        isVisible$: this.navigationService.userHasPermission$(Permission.ManageCompanySettings),
      },
    ]
  }

  /**
   * My Account. These are the signed-in user's own details, so there is no permission to
   * check — every tab is visible to whoever is looking at their own account. Profile Video
   * keeps the feature flag it has always had.
   *
   * Personal Information matches exactly: its route is `/account`, the prefix every other
   * tab here extends.
   */
  private buildAccountTabs(): SectionTab[] {
    return [
      {
        label: 'Personal Information',
        route: ROUTES.USER_PERSONAL,
        exact: true,
        isVisible$: of(true),
      },
      {
        label: 'Decision Team Settings',
        route: ROUTES.HIRING_TEAM_SETTINGS,
        isVisible$: of(true),
      },
      {
        label: 'Profile Picture',
        route: ROUTES.USER_PIC,
        isVisible$: of(true),
      },
      {
        label: 'Profile Video',
        route: ROUTES.USER_VIDEO,
        isVisible$: this.navigationService.features$('employerUserProfileVideo'),
      },
      {
        label: 'Security',
        route: ROUTES.USER_SECURITY,
        isVisible$: of(true),
      },
      {
        label: 'Notifications',
        route: ROUTES.USER_NOTIFICATIONS,
        isVisible$: of(true),
      },
    ]
  }
}
